import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import useAxios from 'axios-hooks'
import { produce } from 'immer'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Col, Container, Flex, Hr, Spacer, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { SortDirection, SortState, TableColumn } from '@amzn/stencil-react-components/table'
import { H1, Text } from '@amzn/stencil-react-components/text'
import { IconSettingsSmall as IconSettings } from '@amzn/stencil-react-icons'

import {
    RenderFlyout,
    SearchBar,
    SearchResultsFilterSidebar,
} from 'src/pages/asset-library/SearchBar'
import {
    defaultPageSize,
    fromParams,
    getSearchRequest,
    toSearchParams,
} from 'src/pages/asset-library/searchQuery'
import { SearchResults } from 'src/pages/asset-library/SearchResults'
import {
    DisplayType,
    MediaTemplate,
    OrderBy,
    PageOfMedia,
    SearchQuery,
} from 'src/pages/asset-library/types'
import { UploadButton } from 'src/pages/asset-library/UploadButton'
import { LAYOUT_DIRECTION, useLayoutDirection } from 'src/pages/search/SearchResultsPage'

export type { MediaTemplate, PageOfMedia, SearchQuery } from 'src/pages/asset-library/types'

function ErrorBanner({ errorMessage }: { errorMessage?: string }) {
    if (errorMessage) {
        return (
            <>
                <MessageBanner
                    type={MessageBannerType.Error}
                    dismissButtonAltText={'Hide errors'}
                    dataTestId={'unexpected-error-banner'}
                >
                    <Text>{errorMessage}</Text>
                </MessageBanner>
            </>
        )
    }

    return null
}

export interface AssetLibraryPageBodyProps {
    actionsColumn?: TableColumn<MediaTemplate>
    displayType: DisplayType
    embedded: boolean
    pageSize?: number
    selectUse?: (mediaVersionId: string) => void
    selectView?: (mediaVersionId: string) => void
    lockedFileTypes?: string[]
}

interface AssetLibraryPageBodyInnerProps extends AssetLibraryPageBodyProps {
    searchQuery: SearchQuery
    setSearchQuery: React.Dispatch<React.SetStateAction<SearchQuery>>
}

export const AssetLibraryPageBodyInner = ({
    actionsColumn,
    displayType,
    embedded,
    pageSize,
    selectUse,
    selectView,
    lockedFileTypes,
    searchQuery,
    setSearchQuery,
}: AssetLibraryPageBodyInnerProps) => {
    const [isFilterOpenForMobileEmbedded, setIsFilterOpenForMobileEmbedded] = useState(false)

    // searchQuery is stored in URL parameters, while nextSearchQuery is the work-in-progress search query
    // calling triggerSearch writes nextSearchQuery back into searchQuery
    const [nextSearchQuery, setNextSearchQuery] = useState<SearchQuery>(searchQuery)
    const [triggerSearchCounter, incrementCounter] = useReducer((x: number) => x + 1, 0)
    const [writeBack, setWriteBack] = useState(false)
    useEffect(() => {
        if (writeBack) {
            setSearchQuery(nextSearchQuery)
            incrementCounter()
            setWriteBack(false)
        }
    }, [writeBack, nextSearchQuery, setSearchQuery])
    const setSearchQueryAndTriggerSearch = useCallback(
        (update: React.SetStateAction<SearchQuery>) => {
            setNextSearchQuery(update)
            setWriteBack(true)
        },
        []
    )

    const onSort = useCallback(
        (state: SortState) => {
            setSearchQueryAndTriggerSearch(
                produce((draft) => {
                    draft.orderBy = state.sortId as OrderBy
                    draft.orderDirection =
                        draft.orderDirection === state.sortDirection
                            ? SortDirection.Ascending
                            : state.sortDirection
                })
            )
        },
        [setSearchQueryAndTriggerSearch]
    )

    const onPageSelect = useCallback(
        (page: number) => {
            setSearchQueryAndTriggerSearch(
                produce((draft) => {
                    draft.pageIndex = page - 1
                })
            )
        },
        [setSearchQueryAndTriggerSearch]
    )

    const resultTableRef = useRef<HTMLDivElement | null>(null)
    const [unexpectedError, setUnexpectedError] = useState<string | undefined>(undefined)
    const [{ loading, data: currentPage, error }, executeSearchRequest] = useAxios<PageOfMedia>(
        {},
        { useCache: false, manual: true }
    )
    useEffect(() => {
        void executeSearchRequest(getSearchRequest(searchQuery))
            .catch((e) => {
                const typeName = (e as Error).constructor.name
                if (typeName === 'Cancel' || typeName === 'CanceledError') {
                    return
                }
                console.error('search error', e)
            })
            .then(() => {
                resultTableRef?.current?.focus()
            })
    }, [triggerSearchCounter, executeSearchRequest, searchQuery])

    const containerRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        containerRef.current?.focus()
    }, [containerRef])

    const layout = useLayoutDirection()
    const isMobileOrTablet =
        layout === LAYOUT_DIRECTION.MOBILE || layout === LAYOUT_DIRECTION.TABLET

    const executeSearch = useCallback(() => {
        setSearchQueryAndTriggerSearch(
            produce((draft) => {
                draft.pageIndex = 0
            })
        )
    }, [setSearchQueryAndTriggerSearch])

    const sidebarPart = useMemo(
        () => (
            <SearchResultsFilterSidebar
                executeSearch={executeSearch}
                lockedFileTypes={lockedFileTypes}
                nextSearchQuery={nextSearchQuery}
                setNextSearchQuery={setNextSearchQuery}
            />
        ),
        [executeSearch, lockedFileTypes, nextSearchQuery]
    )

    if (isFilterOpenForMobileEmbedded) {
        return (
            <Container
                ref={containerRef}
                backgroundColor='#F8F8F8'
                width='100%'
                minHeight='100%'
                paddingHorizontal={0}
            >
                <RenderFlyout
                    close={() => setIsFilterOpenForMobileEmbedded(false)}
                    {...{
                        executeSearch,
                        nextSearchQuery,
                        setNextSearchQuery,
                        lockedFileTypes,
                    }}
                />
            </Container>
        )
    }

    return (
        <Container
            ref={containerRef}
            backgroundColor='#FFF'
            width='100%'
            minHeight='100%'
            paddingHorizontal={0}
        >
            <ErrorBanner errorMessage={unexpectedError} />
            <View padding={{ top: 'S300', left: 'S300', right: 'S300' }}>
                <H1>Asset Library</H1>
            </View>
            <Col gridGap='S300' padding='15px' flex={'1 1 auto'}>
                {displayType === DisplayType.ASSET_LIBRARY_PAGE && (
                    <>
                        <UploadButton setError={setUnexpectedError} />
                        <Spacer height={'S200'} />
                    </>
                )}
                <SearchBar
                    nextSearchQuery={nextSearchQuery}
                    setNextSearchQuery={setNextSearchQuery}
                    displayType={displayType}
                    executeSearch={executeSearch}
                />
                <Hr size='narrow' css={{ color: '#B9C0C8' }} />
                <Flex gridGap='16px' flexDirection={isMobileOrTablet ? 'column' : 'row'}>
                    {isMobileOrTablet && embedded ? (
                        <Button
                            variant={ButtonVariant.Secondary}
                            icon={<IconSettings aria-hidden />}
                            onClick={() => setIsFilterOpenForMobileEmbedded(true)}
                        >
                            Filters
                        </Button>
                    ) : (
                        <View flex={isMobileOrTablet ? '1 1 auto' : '0 0 300px'}>
                            {sidebarPart}
                        </View>
                    )}
                    <View
                        dataTestId={'media-search-result-container'}
                        tabIndex={-1}
                        display={'flex'}
                        flex='3 1 auto'
                        ref={resultTableRef}
                    >
                        <SearchResults
                            loading={loading}
                            error={!!error}
                            currentPage={
                                currentPage ?? {
                                    mediaTemplates: [],
                                    pageIndex: searchQuery.pageIndex,
                                    pageSize: pageSize ?? defaultPageSize,
                                    numberOfPages: 0,
                                    totalNumberOfResults: 0,
                                }
                            }
                            currentSearchQuery={searchQuery}
                            activeSortId={searchQuery.orderBy}
                            sortDirection={searchQuery.orderDirection}
                            onSort={onSort}
                            actionsColumn={actionsColumn}
                            displayType={displayType}
                            onPageSelect={onPageSelect}
                            selectUse={selectUse}
                            selectView={selectView}
                        />
                    </View>
                </Flex>
            </Col>
        </Container>
    )
}

export const AssetLibraryPageBody = ({
    actionsColumn,
    displayType,
    embedded,
    pageSize,
    selectUse,
    selectView,
    lockedFileTypes,
}: AssetLibraryPageBodyProps) => {
    const options = useMemo(
        () => ({
            lockedFileTypes,
            pageSize,
            displayType,
        }),
        [lockedFileTypes, pageSize, displayType]
    )

    const [urlSearchParams, setUrlSearchParams] = useSearchParams()
    const urlSearchQuery = useMemo(
        () => fromParams(urlSearchParams, options),
        [urlSearchParams, options]
    )
    const setUrlSearchQuery = useCallback(
        (newValue: React.SetStateAction<SearchQuery>) => {
            const newSearchQuery =
                typeof newValue === 'function' ? newValue(urlSearchQuery) : newValue
            setUrlSearchParams(toSearchParams(newSearchQuery, options))
        },
        [options, setUrlSearchParams, urlSearchQuery]
    )
    const [reactStateSearchQuery, setReactStateSearchQuery] = useState<SearchQuery>(() =>
        fromParams(new URLSearchParams(), options)
    )

    // If it's embedded in a modal, we use local state to switch between searches, otherwise we use URL query parameters
    const searchQuery = embedded ? reactStateSearchQuery : urlSearchQuery
    const setSearchQuery: React.Dispatch<React.SetStateAction<SearchQuery>> = embedded
        ? setReactStateSearchQuery
        : setUrlSearchQuery

    return (
        <AssetLibraryPageBodyInner
            actionsColumn={actionsColumn}
            displayType={displayType}
            embedded={embedded}
            pageSize={pageSize}
            selectUse={selectUse}
            selectView={selectView}
            lockedFileTypes={lockedFileTypes}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
        />
    )
}

export const AssetLibraryPage = () => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        containerRef.current?.focus()
    }, [])

    return (
        <Container
            ref={containerRef}
            backgroundColor='#FFF'
            width='100%'
            minHeight='100vh'
            paddingHorizontal={0}
        >
            <AssetLibraryPageBody displayType={DisplayType.ASSET_LIBRARY_PAGE} embedded={false} />
        </Container>
    )
}
